var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var _a, _b;
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { createReducer } from '../../reducers/factory';
import { fetchedSliceHasUserData, hasUserData, isRankingItemFetched, } from './types';
import { MINUTE_MS, SECOND_MS } from '../../time';
import { collectionRankingName, compareRankingEntries, getMillisUntilNextRankingRefresh, getMillisUntilNextRefresh, getRankingItemWithoutUserData, } from './utils';
import { collectionsProgressPlainSelector } from './collectionProgress';
import { fetchRanking } from '../../api/collections';
import { NOOP } from '../../utils';
import { radarUserDataSelector, useRadarProfileData, } from '../radarUserProfile';
var FETCH_COLLECTION_RANKING_SUCCESSFUL = 'FETCH_COLLECTION_RANKING_SUCCESSFUL';
var FETCH_COLLECTION_RANKING_FAILED = 'FETCH_COLLECTION_RANKING_FAILED';
var FETCH_COLLECTION_RANKING_IN_PROGRESS = 'FETCH_COLLECTION_RANKING_IN_PROGRESS';
var INVALIDATE = 'INVALIDATE_COLLECTION_RANKING';
var invalidated = function (state) {
    var updatedItems = {};
    Object.keys(state.items).forEach(function (key) {
        updatedItems[key] = __assign(__assign({}, state.items[key]), { didInvalidate: true });
    });
    return __assign(__assign({}, state), { items: updatedItems });
};
var collectionRankingReducer = createReducer('COLLECTION_RANKING', {
    fetchTimeout: 30 * SECOND_MS,
    minUpdate: 10 * MINUTE_MS,
    extendedReducer: (_a = {},
        _a[FETCH_COLLECTION_RANKING_SUCCESSFUL] = function (state, action) {
            var _a;
            return (__assign(__assign({}, state), { items: __assign(__assign({}, state.items), (_a = {}, _a[action.idOrAlias] = __assign(__assign({}, action.collectionRanking), { lastSuccessfulFetch: action.timestamp, fetchPromise: null, fetchingInProgressSince: null, didInvalidate: false }), _a)) }));
        },
        _a[FETCH_COLLECTION_RANKING_FAILED] = function (state, action) {
            var _a;
            return (__assign(__assign({}, state), { items: __assign(__assign({}, state.items), (_a = {}, _a[action.idOrAlias] = __assign(__assign({}, state.items[action.idOrAlias]), { fetchingInProgressSince: null, lastFailedFetch: action.timestamp, fetchPromise: null }), _a)) }));
        },
        _a[FETCH_COLLECTION_RANKING_IN_PROGRESS] = function (state, action) {
            var _a;
            return (__assign(__assign({}, state), { items: __assign(__assign({}, state.items), (_a = {}, _a[action.idOrAlias] = __assign(__assign({}, state.items[action.idOrAlias]), { fetchingInProgressSince: action.timestamp, fetchPromise: action.fetchPromise }), _a)) }));
        },
        _a[INVALIDATE] = function (state) { return invalidated(state); },
        _a.LOGIN_SUCCESSFUL = function (state) { return invalidated(state); },
        _a.LOGOUT_SUCCESSFUL = function (state) {
            var newItems = {};
            Object.keys(state.items).forEach(function (idOrAlias) {
                var item = state.items[idOrAlias];
                if (isRankingItemFetched(item) && fetchedSliceHasUserData(item)) {
                    newItems[idOrAlias] = getRankingItemWithoutUserData(item);
                }
                else {
                    newItems[idOrAlias] = item;
                }
            });
            return __assign(__assign({}, state), { items: newItems });
        },
        _a),
});
var fetchRankingSuccessful = function (collectionRanking, idOrAlias) { return ({
    type: FETCH_COLLECTION_RANKING_SUCCESSFUL,
    collectionRanking: collectionRanking,
    timestamp: Date.now(),
    idOrAlias: idOrAlias,
}); };
collectionRankingReducer.fetchFailed = function (reason, idOrAlias) { return ({
    type: FETCH_COLLECTION_RANKING_FAILED,
    reason: reason,
    timestamp: Date.now(),
    idOrAlias: idOrAlias,
}); };
collectionRankingReducer.fetchInProgress = function (fetchPromise, idOrAlias) { return ({
    type: FETCH_COLLECTION_RANKING_IN_PROGRESS,
    fetchPromise: fetchPromise,
    timestamp: Date.now(),
    idOrAlias: idOrAlias,
}); };
export var rankingsSelector = function (_a) {
    var _b = collectionRankingName, rankings = _a[_b];
    return rankings.items;
};
export var rankingSelector = function (state, idOrAlias) {
    var _a;
    var rankings = rankingsSelector(state);
    var aliases = collectionsProgressPlainSelector(state).aliases;
    var rankingId = (_a = aliases[idOrAlias]) !== null && _a !== void 0 ? _a : idOrAlias;
    return rankings[rankingId];
};
export var rankingWithUserDataSelector = function (state, idOrAlias) {
    var _a, _b, _c;
    var ranking = rankingSelector(state, idOrAlias);
    if (!isRankingItemFetched(ranking)) {
        return ranking;
    }
    if (!hasUserData(ranking)) {
        return __assign(__assign({}, ranking), { top: ranking.top.map(function (rankingEntry) { return (__assign(__assign({}, rankingEntry), { isUserEntry: false })); }) });
    }
    // @ts-ignore radar store is not typed
    var _d = ((_a = radarUserDataSelector(state)) !== null && _a !== void 0 ? _a : {}).userData, userData = _d === void 0 ? {} : _d;
    var userEntry = ranking.rank;
    var assignChatRadarAvatarToUserEntry = function (rankingEntry) {
        var _a, _b;
        var isUserEntry = userEntry && compareRankingEntries(rankingEntry, userEntry);
        return __assign(__assign({}, rankingEntry), { isUserEntry: isUserEntry, avatar: isUserEntry
                ? (_b = (_a = userData === null || userData === void 0 ? void 0 : userData.avatar) === null || _a === void 0 ? void 0 : _a.path) !== null && _b !== void 0 ? _b : undefined
                : rankingEntry.avatar });
    };
    return __assign(__assign({}, ranking), { top: ranking.top.map(assignChatRadarAvatarToUserEntry), related: ranking.related.map(assignChatRadarAvatarToUserEntry), rank: userEntry
            ? __assign(__assign({}, userEntry), { isUserEntry: true, avatar: (_c = (_b = userData === null || userData === void 0 ? void 0 : userData.avatar) === null || _b === void 0 ? void 0 : _b.path) !== null && _c !== void 0 ? _c : undefined }) : null });
};
export var fetchCollectionRanking = function (idOrAlias) { return function (dispatch, getState) {
    var _a = getState().auth, token = _a.token, apiUrl = _a.apiUrl;
    var fetchPromise = fetchRanking(apiUrl, token, idOrAlias)
        .then(function (ranking) {
        dispatch(fetchRankingSuccessful(ranking, idOrAlias));
        return ranking;
    })
        .catch(function (e) {
        dispatch(collectionRankingReducer.fetchFailed(e, idOrAlias));
        throw e;
    });
    dispatch(collectionRankingReducer.fetchInProgress(fetchPromise, idOrAlias));
    return fetchPromise;
}; };
export var fetchCollectionRankingIfNeeded = function (idOrAlias) { return function (dispatch, getState) {
    var _a;
    var ranking = rankingSelector(getState(), idOrAlias);
    var wasProbablyRefreshedSinceLastFetch = isRankingItemFetched(ranking) &&
        getMillisUntilNextRankingRefresh(ranking) <= 0;
    if (collectionRankingReducer.shouldFetch(ranking) ||
        wasProbablyRefreshedSinceLastFetch) {
        return dispatch(fetchCollectionRanking(idOrAlias));
    }
    return ((_a = ranking.fetchPromise) !== null && _a !== void 0 ? _a : Promise.resolve(ranking));
}; };
export var useCollectionRanking = function (idOrAlias, onError) {
    if (onError === void 0) { onError = NOOP; }
    var ranking = useSelector(function (state) { return rankingWithUserDataSelector(state, idOrAlias); });
    var dispatch = useDispatch();
    useRadarProfileData();
    useEffect(function () {
        if (idOrAlias) {
            dispatch(fetchCollectionRankingIfNeeded(idOrAlias)).catch(onError);
        }
    }, [dispatch, idOrAlias, onError, ranking === null || ranking === void 0 ? void 0 : ranking.didInvalidate]);
    var millisUntilNextRefresh = useMemo(function () {
        if (!ranking ||
            !isRankingItemFetched(ranking) ||
            ranking.nextRefreshEstimate < 0) {
            return null;
        }
        return getMillisUntilNextRefresh(ranking.nextRefreshEstimate);
    }, [ranking]);
    useEffect(function () {
        if (!millisUntilNextRefresh || millisUntilNextRefresh < 0) {
            // If the ranking we have is outdated, the `fetchCollectionRankingIfNeeded` action will update it
            return NOOP;
        }
        var timeout = setTimeout(function () {
            dispatch(fetchCollectionRanking(idOrAlias)).catch(onError);
        }, millisUntilNextRefresh);
        return function () {
            clearTimeout(timeout);
        };
    }, [dispatch, idOrAlias, millisUntilNextRefresh, onError]);
    return ranking && isRankingItemFetched(ranking) ? ranking : undefined;
};
export default (_b = {}, _b[collectionRankingName] = collectionRankingReducer, _b);
